import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Utensils } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@project/components/ui/radio-group";
import { MobileNumberField } from "@project/components/mobile-number-field";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { getApiErrorMessage } from "@project/lib/api-error";
import { formatPreferredTithiOption } from "@project/lib/jain-panchang-utils";
import { projectToast } from "@project/lib/toast";
import { bhojanDonationService } from "@project/services/admin/bhojan-donation.service";
import { bhojanService } from "@project/services/admin/bhojan.service";
import { jainPanchangService } from "@project/services/admin/jain-panchang.service";
import {
  BHOJAN_OCCASION_OPTIONS,
  BHOJAN_OFFLINE_PAYMENT_MODE_OPTIONS,
  formatBhojanPrice,
  toBhojanPrice,
  type BhojanAddDonationPayload,
} from "@project/types/bhojan";
import type { JainPanchang } from "@project/types/jain-panchang";

type FormState = {
  bhojan_id: string;
  preferred_sponsorship_date: string;
  preferred_tithi: string;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  state: string;
  pincode: string;
  country: string;
  occasion: string;
  display_name: string;
  special_instructions: string;
  is_anonymous: boolean;
  info_confirmed: boolean;
  contact_consent: boolean;
  amount: string;
  payment_mode: string;
  payment_reference: string;
};

const initialForm: FormState = {
  bhojan_id: "",
  preferred_sponsorship_date: "",
  preferred_tithi: "",
  donor_name: "",
  mobile_number: "",
  email: "",
  city: "Ahmedabad",
  full_address: "",
  state: "Gujarat",
  pincode: "",
  country: "India",
  occasion: "Birthday",
  display_name: "",
  special_instructions: "",
  is_anonymous: false,
  info_confirmed: true,
  contact_consent: true,
  amount: "",
  payment_mode: "cash",
  payment_reference: "",
};

type BhojanAddDonationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function BhojanAddDonationDialog({
  open,
  onOpenChange,
  onSuccess,
}: BhojanAddDonationDialogProps) {
  const [form, setForm] = useState<FormState>(initialForm);
  const [packageLabel, setPackageLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tithiOptions, setTithiOptions] = useState<JainPanchang[]>([]);
  const [tithiLoading, setTithiLoading] = useState(false);
  const [tithiError, setTithiError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setPackageLabel("");
      setSubmitting(false);
      setTithiOptions([]);
      setTithiError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !form.preferred_sponsorship_date) {
      setTithiOptions([]);
      setTithiError(null);
      return;
    }

    let ignore = false;
    const loadTithis = async () => {
      setTithiLoading(true);
      setTithiError(null);
      setForm(prev =>
        prev.preferred_tithi ? { ...prev, preferred_tithi: "" } : prev
      );
      try {
        const result = await jainPanchangService.listJainPanchangPage({
          page: 1,
          page_size: 20,
          english_date: form.preferred_sponsorship_date,
        });
        if (ignore) {
          return;
        }
        const entries = (result.data ?? []).slice().sort((a, b) => {
          const orderDiff = (a.display_order ?? 0) - (b.display_order ?? 0);
          if (orderDiff !== 0) {
            return orderDiff;
          }
          return a.id - b.id;
        });
        setTithiOptions(entries);
        if (entries.length === 1) {
          const only = formatPreferredTithiOption(entries[0]);
          if (only) {
            setForm(prev => ({ ...prev, preferred_tithi: only }));
          }
        }
      } catch {
        if (!ignore) {
          setTithiOptions([]);
          setTithiError("Could not load tithi options for this date.");
        }
      } finally {
        if (!ignore) {
          setTithiLoading(false);
        }
      }
    };

    void loadTithis();
    return () => {
      ignore = true;
    };
  }, [open, form.preferred_sponsorship_date]);

  const patch = (updates: Partial<FormState>) =>
    setForm(prev => ({ ...prev, ...updates }));

  const tithiRadioOptions = useMemo(
    () =>
      tithiOptions
        .map(entry => ({
          id: entry.id,
          value: formatPreferredTithiOption(entry),
          title: formatPreferredTithiOption({ ...entry, type: undefined }),
          type: entry.type === "hindu" ? "Hindu" : "Jain",
          typeKey: entry.type === "hindu" ? "hindu" : "jain",
        }))
        .filter(option => option.value),
    [tithiOptions]
  );

  const searchPackages = useCallback(async (query: string) => {
    const result = await bhojanService.listPage({
      page: 1,
      page_size: 50,
      search: query || undefined,
    });
    return (result.data ?? [])
      .filter(pkg => pkg.is_active !== false)
      .map(pkg => ({
        value: String(pkg.id),
        label: pkg.name,
        secondary: [pkg.category_name, formatBhojanPrice(pkg.price)]
          .filter(Boolean)
          .join(" · "),
      }));
  }, []);

  const buildPayload = (): BhojanAddDonationPayload => {
    const bhojanId = Number(form.bhojan_id);
    if (!Number.isFinite(bhojanId) || bhojanId <= 0) {
      throw new Error("Please select a meal package.");
    }
    if (!form.preferred_sponsorship_date) {
      throw new Error("Preferred sponsorship date is required.");
    }
    if (!form.donor_name.trim()) {
      throw new Error("Donor name is required.");
    }
    if (!/^\d{10}$/.test(form.mobile_number.trim())) {
      throw new Error("Enter a valid 10-digit mobile number.");
    }
    if (!form.email.trim()) {
      throw new Error("Email is required.");
    }
    if (!form.city.trim()) {
      throw new Error("City is required.");
    }
    if (!form.full_address.trim()) {
      throw new Error("Full address is required.");
    }
    if (!form.state.trim()) {
      throw new Error("State is required.");
    }
    if (!/^\d{6}$/.test(form.pincode.trim())) {
      throw new Error("Enter a valid 6-digit pincode.");
    }
    if (!form.payment_mode) {
      throw new Error("Payment mode is required.");
    }
    if (!form.info_confirmed) {
      throw new Error("Please confirm the donation information.");
    }
    if (tithiRadioOptions.length > 0 && !form.preferred_tithi.trim()) {
      throw new Error("Please select a preferred tithi.");
    }

    return {
      bhojan_id: bhojanId,
      preferred_sponsorship_date: form.preferred_sponsorship_date,
      preferred_tithi: form.preferred_tithi.trim() || undefined,
      donor_name: form.donor_name.trim(),
      mobile_number: form.mobile_number.trim(),
      email: form.email.trim(),
      city: form.city.trim(),
      full_address: form.full_address.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      country: form.country.trim() || "India",
      occasion: form.occasion || undefined,
      display_name: form.display_name.trim() || undefined,
      special_instructions: form.special_instructions.trim() || undefined,
      is_anonymous: form.is_anonymous,
      info_confirmed: form.info_confirmed,
      contact_consent: form.contact_consent,
      amount: toBhojanPrice(form.amount),
      mode: "offline",
      payment_mode: form.payment_mode,
      payment_reference: form.payment_reference.trim() || undefined,
    };
  };

  const handleSubmit = async () => {
    try {
      const payload = buildPayload();
      setSubmitting(true);
      await bhojanDonationService.add(payload);
      projectToast.success("Bhojanshala donation added successfully");
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      projectToast.error(getApiErrorMessage(error, "Could not add donation"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
        <DialogHeader className="shrink-0 border-b border-border px-5 py-4 pr-12 text-left sm:px-6">
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-primary">
              <Utensils className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold sm:text-xl">
                Add Bhojanshala Donation
              </DialogTitle>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Record an offline meal sponsorship donation
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
          <section className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label data-required="true">Meal Package</Label>
              <SearchableSelect
                value={form.bhojan_id || undefined}
                onValueChange={(value, option) => {
                  const next: Partial<FormState> = { bhojan_id: value };
                  setPackageLabel(option?.label ?? "");
                  void bhojanService.get(Number(value)).then(pkg => {
                    if (pkg?.price !== null) {
                      const amount = Number(pkg.price);
                      if (Number.isFinite(amount)) {
                        setForm(prev => ({
                          ...prev,
                          bhojan_id: value,
                          amount: String(amount),
                        }));
                      }
                    }
                  });
                  patch(next);
                }}
                onSearch={searchPackages}
                seedOption={
                  form.bhojan_id
                    ? {
                        value: form.bhojan_id,
                        label: packageLabel || `Package #${form.bhojan_id}`,
                      }
                    : undefined
                }
                placeholder="Select package..."
                searchPlaceholder="Search packages..."
                emptyText="No packages found."
                className="mt-1"
              />
            </div>

            <div>
              <Label data-required="true">Preferred Sponsorship Date</Label>
              <Input
                type="date"
                value={form.preferred_sponsorship_date}
                onChange={e =>
                  patch({
                    preferred_sponsorship_date: e.target.value,
                    preferred_tithi: "",
                  })
                }
                className="mt-1 rounded-xl"
              />
            </div>

            <div className="sm:col-span-2">
              <Label data-required={tithiRadioOptions.length > 0}>
                Preferred Tithi
              </Label>
              {!form.preferred_sponsorship_date ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  Select a sponsorship date to load tithi options.
                </p>
              ) : tithiLoading ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  Loading tithi options…
                </p>
              ) : tithiError ? (
                <p className="mt-2 text-sm text-destructive">{tithiError}</p>
              ) : tithiRadioOptions.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  No tithi entries found for this date.
                </p>
              ) : (
                <RadioGroup
                  value={form.preferred_tithi}
                  onValueChange={preferred_tithi => patch({ preferred_tithi })}
                  className="mt-2 grid gap-3 sm:grid-cols-2"
                >
                  {tithiRadioOptions.map(option => {
                    const selected = form.preferred_tithi === option.value;
                    return (
                      <label
                        key={option.id}
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 text-sm transition-colors ${
                          selected
                            ? "bg-primary/[0.05] border-primary"
                            : "border-border/70 hover:bg-muted/40 bg-card"
                        }`}
                      >
                        <RadioGroupItem
                          value={option.value}
                          className="mt-0.5"
                        />
                        <span className="min-w-0">
                          <span className="block font-medium leading-snug text-foreground">
                            {option.title}
                          </span>
                          <span
                            className={`mt-2 inline-flex h-5 items-center px-2.5 text-[10px] font-bold uppercase tracking-wide text-white ${
                              option.typeKey === "hindu"
                                ? "bg-orange-500"
                                : "bg-primary"
                            }`}
                            style={{
                              clipPath:
                                "polygon(0 0, calc(100% - 5px) 0, 100% 50%, calc(100% - 5px) 100%, 0 100%)",
                            }}
                          >
                            {option.type}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </RadioGroup>
              )}
            </div>

            <div>
              <Label data-required="true">Amount (₹)</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={form.amount}
                onChange={e => patch({ amount: e.target.value })}
                placeholder="51000"
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label data-required="true">Payment Mode</Label>
              <Select
                value={form.payment_mode || undefined}
                onValueChange={value => patch({ payment_mode: value })}
              >
                <SelectTrigger className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select payment mode..." />
                </SelectTrigger>
                <SelectContent>
                  {BHOJAN_OFFLINE_PAYMENT_MODE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Occasion</Label>
              <Select
                value={form.occasion || undefined}
                onValueChange={value => patch({ occasion: value })}
              >
                <SelectTrigger className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select occasion..." />
                </SelectTrigger>
                <SelectContent>
                  {BHOJAN_OCCASION_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Payment Reference</Label>
              <Input
                value={form.payment_reference}
                onChange={e => patch({ payment_reference: e.target.value })}
                placeholder="Cheque / UPI / NEFT reference"
                className="mt-1 rounded-xl"
              />
            </div>
          </section>

          <section>
            <h3 className="mb-3 font-serif text-lg font-semibold text-foreground">
              Donor Details
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label data-required="true">Donor Name</Label>
                <Input
                  value={form.donor_name}
                  onChange={e => patch({ donor_name: e.target.value })}
                  placeholder="Meena Shah"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Display Name</Label>
                <Input
                  value={form.display_name}
                  onChange={e => patch({ display_name: e.target.value })}
                  placeholder="Shah Family"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">Mobile Number</Label>
                <MobileNumberField
                  required
                  value={form.mobile_number}
                  onChange={mobile_number => patch({ mobile_number })}
                  placeholder="10-digit phone number"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => patch({ email: e.target.value })}
                  placeholder="meena@example.com"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="sm:col-span-2">
                <Label data-required="true">Full Address</Label>
                <Textarea
                  value={form.full_address}
                  onChange={e => patch({ full_address: e.target.value })}
                  placeholder="12 Shanti Nagar"
                  className="mt-1 rounded-xl"
                  rows={2}
                />
              </div>
              <div>
                <Label data-required="true">City</Label>
                <Input
                  value={form.city}
                  onChange={e => patch({ city: e.target.value })}
                  placeholder="Ahmedabad"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">State</Label>
                <Input
                  value={form.state}
                  onChange={e => patch({ state: e.target.value })}
                  placeholder="Gujarat"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">Pincode</Label>
                <Input
                  value={form.pincode}
                  onChange={e => patch({ pincode: e.target.value })}
                  placeholder="380001"
                  maxLength={6}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={form.country}
                  onChange={e => patch({ country: e.target.value })}
                  placeholder="India"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Special Instructions</Label>
                <Textarea
                  value={form.special_instructions}
                  onChange={e =>
                    patch({ special_instructions: e.target.value })
                  }
                  placeholder="Optional notes"
                  className="mt-1 rounded-xl"
                  rows={2}
                />
              </div>
            </div>
          </section>

          <section className="bg-muted/20 space-y-3 rounded-xl border border-border p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={form.is_anonymous}
                onCheckedChange={checked =>
                  patch({ is_anonymous: checked === true })
                }
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">
                Record as anonymous donation
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={form.info_confirmed}
                onCheckedChange={checked =>
                  patch({ info_confirmed: checked === true })
                }
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">
                Information confirmed{" "}
                <span className="text-destructive">*</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={form.contact_consent}
                onCheckedChange={checked =>
                  patch({ contact_consent: checked === true })
                }
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">
                Contact consent for this sponsorship
              </span>
            </label>
          </section>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t border-border px-5 py-4 sm:px-6">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="gap-1.5 rounded-xl"
            disabled={submitting}
            onClick={() => void handleSubmit()}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {submitting ? "Saving…" : "Add Donation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
